<template>
  <component :is="rootTag" v-if="hasData || loading" :class="rootClass">
    <div v-if="!compact">
      <div class="flex items-center gap-1.5">
        <TextTitle>Eigen portfolio voordeel</TextTitle>
        <BaseTooltip :description="tooltipDescription" />
      </div>
      <TextDescription>
        Voordeel over de geselecteerde periode dat is behaald door je eigen
        portfolio te bundelen.
      </TextDescription>
    </div>
    <LoadingWrapper :loading :error="loadingFailed">
      <div :class="compact ? '' : 'flex flex-wrap items-end gap-6'">
        <KpiComponent
          label="Eigen portfolio voordeel"
          :value="benefit ?? 0"
          :precision="2"
          format="€ _"
          icon="stack"
          icon-color="primary"
        >
          <template #description="{ label }">
            <span class="inline-flex flex-row items-center gap-1">
              {{ label }}
              <!-- In compact mode the info tooltip lives inside the box -->
              <BaseTooltip v-if="compact" :description="tooltipDescription" />
            </span>
          </template>
        </KpiComponent>
      </div>
    </LoadingWrapper>
  </component>
</template>

<script setup>
import { computed } from "vue"
import useResultsData from "@/composables/useResultsData.js"
import {
  BaseCard,
  BaseTooltip,
  TextTitle,
  TextDescription,
  LoadingWrapper,
} from "@repowerednl/ui-component-library"
import KpiComponent from "@/components/information/KpiComponent.vue"

const props = defineProps({
  meteringPoint: { type: Object, default: null },
  dateRange: { type: Array, required: true },
  /**
   * When compact (e.g. in a card header next to the export button) only the KPI
   * box is rendered: no title or subtitle, and the info tooltip moves into the
   * box itself.
   */
  compact: { type: Boolean, default: false },
})

const BENEFIT_RECORD_TYPE = "PORTFOLIO_IMBALANCE_BENEFIT"

const tooltipDescription =
  "Het financiële voordeel dat je behaalt door je eigen portfolio te bundelen, vergeleken met losse afhandeling."

const rootTag = computed(() => (props.compact ? "div" : BaseCard))

const rootClass = computed(() =>
  props.compact ? "" : "flex flex-col gap-2 p-4 md:gap-4 md:p-6",
)

const { recordsData, loading, loadingFailed } = useResultsData(
  () => props.meteringPoint,
  () => props.dateRange,
)

const benefit = computed(() => {
  const value = recordsData.value?.totals?.data?.[BENEFIT_RECORD_TYPE]?.value
  return value ?? null
})

const hasData = computed(() => benefit.value !== null)
</script>
